import { Button } from '@mui/material';
import { toastError } from '@onestep/coreui/Toast';
import classNames from 'classnames';
import { noop } from 'lodash';
import { observer } from 'mobx-react-lite';
import { createContext, useEffect, useState } from 'react';

import { LoadingPlaceholder } from '../../../components/LoadingPlaceholder';
import MixpanelProvider from '../../../hooks/useMixpanel/MixpanelProvider';
import useStores from '../../../hooks/useStores';
import useClinicalInfoService from '../../../services/patientProfileService/clinicalInfo';
import useRtmEventsService from '../ClinicalEvents/service';
import EventsList from './EventsList';
import styles from './index.module.scss';
import NewEventModal, { eventType } from './NewEventModal';

type eventIdType = number | 'none' | undefined | string;
export const eventModalContext = createContext<{
  setEventUuid(uuid?: eventIdType): void;
  eventUuid?: eventIdType;
  setEventTab(tab?: eventType): void;
  eventTab?: eventType;
}>({
  setEventUuid: noop,
  setEventTab: noop,
});
function PatientTimeline({ patientUuid }: { patientUuid: string }) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { meetingsStore } = useStores();
  const [eventId, setEventId] = useState<eventIdType>();
  const [selectedTab, setSelectedTab] = useState<eventType>(eventType.surgery);
  const { isLoading: isLoadingEvents } = useRtmEventsService(patientUuid);
  const { isLoading: isLoadingFallEvents } = useClinicalInfoService(patientUuid);
  async function loadPageData() {
    setIsLoading(true);
    try {
      await meetingsStore.fetchPatientMeetings(patientUuid);
    } catch {
      toastError({ content: 'Failed fetching all timeline data!' });
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (patientUuid) {
      loadPageData();
    }
  }, [patientUuid]);

  const addEvent = () => {
    setEventId('none');
    setSelectedTab(eventType.surgery);
  };

  return (
    <MixpanelProvider mixpanel={{ screen: 'timeline', patientUuid }}>
      <eventModalContext.Provider
        value={{
          eventUuid: eventId,
          eventTab: selectedTab,
          setEventTab: (tab: eventType) => setSelectedTab(tab),
          setEventUuid: (id?: eventIdType) => setEventId(id),
        }}
      >
        <LoadingPlaceholder isLoading={isLoading || isLoadingEvents || isLoadingFallEvents}>
          <div className={classNames('relative', styles.root)}>
            <div className="flex flex-row pb-8 justify-between">
              <div>
                <EventsList />
              </div>
              <Button
                variant="contained"
                color="primary"
                onClick={addEvent}
                classes={{ root: styles.addBtn }}
              >
                Add event
              </Button>
            </div>
          </div>
        </LoadingPlaceholder>
        <NewEventModal key={eventId} />
      </eventModalContext.Provider>
    </MixpanelProvider>
  );
}
export default observer(PatientTimeline);
